// El calendario en el teléfono — ADR-2026-10-07-el-calendario-en-el-telefono.
//
// En 390px la grilla de siete columnas con píldoras no entra, y la pantalla de escritorio
// apilada dejaba el calendario abajo del pliegue. Esto es otra forma, tomada de Todoist,
// Structured y Saturn en Mobbin:
//   · chips con contador que FILTRAN, en vez de cuatro tarjetas de números;
//   · una semana con puntos de color, que se expande al mes compacto tocando el título;
//   · la lista por día desde el día elegido, con un "+" en cada día que viene;
//   · "Nuevo post" como botón flotante, a mano del pulgar.

import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Plus, Sparkles } from 'lucide-react';
import { correrMes, grillaDelMes, horaDe, mesDe, tituloDelMes } from './calendario';
import { IconoDeFormato, Miniatura, TONOS, tonoDe, type Tono } from './estadoDelPost';
import type { PostConMiniaturas } from './postsApi';

const LETRAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

type Filtro = 'todos' | Tono;

const aMedianoche = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const mismoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** El día que se elige solo al entrar a un mes: hoy si cae ahí, si no el primero. */
function diaInicial(mes: Date): Date {
  const hoy = new Date();
  return hoy.getFullYear() === mes.getFullYear() && hoy.getMonth() === mes.getMonth()
    ? aMedianoche(hoy)
    : new Date(mes.getFullYear(), mes.getMonth(), 1);
}

export function CalendarioMovil({
  posts,
  mes,
  onCambiarMes,
  delAgente,
  onAbrir,
  onNuevo,
}: {
  posts: PostConMiniaturas[];
  mes: Date;
  onCambiarMes: (mes: Date) => void;
  delAgente: Set<string>;
  onAbrir: (id: string) => void;
  /** Crear un post: en ese día (la hora la sugiere el calendario) o donde convenga. */
  onNuevo: (dia: Date | null) => void;
}) {
  const [dia, setDia] = useState(() => diaInicial(mes));
  const [expandido, setExpandido] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('todos');

  // El mes cambió desde afuera (el agente lo llevó a otro, "Hoy"): el día elegido lo sigue.
  useEffect(() => {
    if (dia.getFullYear() !== mes.getFullYear() || dia.getMonth() !== mes.getMonth()) setDia(diaInicial(mes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes]);

  const hoy = aMedianoche(new Date());
  const postsDelDia = (d: Date) => posts.filter((p) => p.cuando && mismoDia(p.cuando, d));

  const delMes = useMemo(
    () =>
      posts
        .filter(
          (p) =>
            p.cuando &&
            p.cuando.getFullYear() === mes.getFullYear() &&
            p.cuando.getMonth() === mes.getMonth(),
        )
        .sort((a, b) => a.cuando!.getTime() - b.cuando!.getTime()),
    [posts, mes],
  );

  const cuentas = useMemo(() => {
    const c: Record<Tono, number> = { listo: 0, falta: 0, publicado: 0, error: 0 };
    for (const p of delMes) c[tonoDe(p)] += 1;
    return c;
  }, [delMes]);

  const chips: Array<{ id: Filtro; texto: string; n: number }> = [
    { id: 'todos', texto: 'Todos', n: delMes.length },
    { id: 'listo', texto: 'Listos', n: cuentas.listo },
    { id: 'falta', texto: 'A completar', n: cuentas.falta },
    { id: 'publicado', texto: 'Publicados', n: cuentas.publicado },
    ...(cuentas.error > 0 ? [{ id: 'error' as Filtro, texto: 'No se publicaron', n: cuentas.error }] : []),
  ];

  // La semana del día elegido, de lunes a domingo.
  const lunes = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate() - ((dia.getDay() + 6) % 7));
  const semana = Array.from(
    { length: 7 },
    (_, i) => new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + i),
  );

  const elegir = (d: Date) => {
    setDia(aMedianoche(d));
    if (d.getMonth() !== mes.getMonth() || d.getFullYear() !== mes.getFullYear()) onCambiarMes(mesDe(d));
  };

  const correr = (sentido: -1 | 1) => {
    if (expandido) {
      const otro = correrMes(mes, sentido);
      onCambiarMes(otro);
      setDia(diaInicial(otro));
    } else {
      elegir(new Date(dia.getFullYear(), dia.getMonth(), dia.getDate() + 7 * sentido));
    }
  };

  // La lista: lo del mes desde el día elegido, con el filtro. El día elegido aparece aunque
  // esté vacío, para poder sumarle algo.
  const grupos = useMemo(() => {
    const desde = dia.getTime();
    const filtrados = delMes.filter(
      (p) => p.cuando!.getTime() >= desde && (filtro === 'todos' || tonoDe(p) === filtro),
    );
    const porDia = new Map<string, { fecha: Date; posts: PostConMiniaturas[] }>();
    porDia.set(dia.toDateString(), { fecha: dia, posts: [] });
    for (const p of filtrados) {
      const k = p.cuando!.toDateString();
      const g = porDia.get(k) ?? { fecha: aMedianoche(p.cuando!), posts: [] };
      g.posts.push(p);
      porDia.set(k, g);
    }
    return [...porDia.values()].sort((a, b) => a.fecha.getTime() - b.fecha.getTime());
  }, [delMes, dia, filtro]);

  const grilla = useMemo(() => grillaDelMes(mes, posts, new Date()), [mes, posts]);

  const puntos = (lista: PostConMiniaturas[]) => (
    <span className="flex h-1.5 gap-0.5">
      {lista.slice(0, 3).map((p) => (
        <span key={p.id} className={`h-1.5 w-1.5 rounded-full ${TONOS[tonoDe(p)].punto}`} />
      ))}
    </span>
  );

  return (
    <div className="md:hidden">
      {/* Los números del mes, como filtros: tocar "A completar" deja sólo esos. */}
      <div
        data-tour="ig-chips"
        className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none]"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setFiltro(c.id)}
            aria-pressed={filtro === c.id}
            className={`flex shrink-0 items-center gap-1.5 rounded-full py-1.5 px-3 text-xs font-medium transition-colors ${
              filtro === c.id
                ? 'bg-brand-black text-white'
                : 'bg-white text-neutral-600 ring-1 ring-inset ring-neutral-200'
            }`}
          >
            {c.id !== 'todos' && <span className={`h-1.5 w-1.5 rounded-full ${TONOS[c.id].punto}`} />}
            {c.texto}
            <span className={filtro === c.id ? 'text-white/70' : 'text-neutral-400'}>{c.n}</span>
          </button>
        ))}
      </div>

      {/* El mes: el título lo expande y lo achica (Todoist). */}
      <div
        data-tour="ig-semana"
        className="mt-3 rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm"
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpandido((e) => !e)}
            aria-expanded={expandido}
            className="flex min-w-0 items-center gap-1 rounded-lg py-1 pr-1 text-base font-semibold capitalize text-brand-black"
          >
            {tituloDelMes(mes)}
            <ChevronDown
              className={`w-4 h-4 text-neutral-400 transition-transform ${expandido ? 'rotate-180' : ''}`}
            />
          </button>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => elegir(new Date())}
            className="rounded-lg bg-neutral-100 py-1.5 px-2.5 text-xs font-medium text-brand-black"
          >
            Hoy
          </button>
          <button
            type="button"
            onClick={() => correr(-1)}
            aria-label={expandido ? 'Mes anterior' : 'Semana anterior'}
            className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => correr(1)}
            aria-label={expandido ? 'Mes siguiente' : 'Semana siguiente'}
            className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-2 grid grid-cols-7 text-center">
          {LETRAS.map((l, i) => (
            <span key={i} className="pb-1 text-[10px] font-semibold uppercase text-neutral-400">
              {l}
            </span>
          ))}
          {(expandido ? grilla.map((g) => g.fecha) : semana).map((d) => {
            const elegido = mismoDia(d, dia);
            const esHoy = mismoDia(d, hoy);
            const fuera = d.getMonth() !== mes.getMonth();
            return (
              <button
                key={d.toISOString()}
                type="button"
                onClick={() => elegir(d)}
                className="flex flex-col items-center gap-1 py-1"
              >
                <span
                  className={`grid h-8 w-8 place-items-center rounded-full text-sm font-medium ${
                    elegido
                      ? 'bg-brand-primary text-white'
                      : esHoy
                        ? 'text-brand-primary ring-1 ring-inset ring-[rgb(var(--brand-primary-rgb)_/_0.225)]'
                        : fuera && expandido
                          ? 'text-neutral-300'
                          : d < hoy
                            ? 'text-neutral-400'
                            : 'text-brand-black'
                  }`}
                >
                  {d.getDate()}
                </span>
                {puntos(postsDelDia(d))}
              </button>
            );
          })}
        </div>
      </div>

      {/* La lista, desde el día elegido. */}
      <div className="mt-4 space-y-4 pb-24">
        {grupos.map((g) => {
          const pasado = g.fecha < hoy;
          return (
            <section key={g.fecha.toDateString()}>
              <div className="mb-2 flex items-center gap-2">
                <h3 className="flex-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 first-letter:uppercase">
                  {mismoDia(g.fecha, hoy) ? 'Hoy · ' : ''}
                  {g.fecha
                    .toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
                    .replace(',', '')}
                </h3>
                {!pasado && (
                  <button
                    type="button"
                    onClick={() => onNuevo(g.fecha)}
                    aria-label="Nuevo post este día"
                    className="grid h-7 w-7 place-items-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
              {g.posts.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-neutral-200 py-3 px-4 text-sm text-neutral-400">
                  {filtro === 'todos' ? 'Nada planificado este día.' : 'Nada con este filtro este día.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {g.posts.map((p) => {
                    const tono = tonoDe(p);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onAbrir(p.id)}
                        className="flex w-full items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-2.5 text-left shadow-sm active:bg-neutral-50"
                      >
                        <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-neutral-100">
                          <Miniatura post={p} icono="w-4 h-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-black">
                            {horaDe(p)}
                            <IconoDeFormato post={p} className="w-3.5 h-3.5 text-neutral-400" />
                            {delAgente.has(p.id) && <Sparkles className="w-3.5 h-3.5 text-brand-primary" />}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-neutral-500">
                            {p.texto.trim() || 'Sin descripción todavía'}
                          </span>
                        </span>
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${TONOS[tono].punto}`}
                          aria-label={TONOS[tono].texto}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
        {grupos.length === 1 && grupos[0]!.posts.length === 0 && delMes.length === 0 && (
          <div className="flex items-center gap-2 px-1 text-sm text-neutral-500">
            <CalendarDays className="w-4 h-4 text-neutral-300" />
            Este mes todavía no tiene posts.
          </div>
        )}
      </div>

      {/* "Nuevo post" flotante. Las dos pantallas que lo muestran (`/instagram` y la ficha de
          `/lanzamientos/:id`) van sin la barra de abajo de la app, así que se apoya en el borde
          (ADR-2026-11-02-instagram-sin-barra-en-el-telefono). */}
      <button
        type="button"
        onClick={() => onNuevo(dia >= hoy ? dia : null)}
        data-tour="ig-nuevo-movil"
        aria-label="Nuevo post"
        className="fixed right-4 z-30 grid h-14 w-14 place-items-center rounded-full bg-brand-primary text-white shadow-[0_10px_24px_-6px_rgb(var(--brand-primary-rgb)_/_0.6)] active:scale-95"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}
